<template>
  <v-container fluid>
    <div class="container">
      <h1 class="subs-title">{{ adminText('organizationAll.create_a_new_theater_subscription_plan_2c3578f9', 'Create a new Theater Subscription Plan') }}</h1>
      <p class="page-description">
        {{ adminText('organizationAll.define_a_new_subscription_plan_by_setting_its_d27bdc08', 'Define a new subscription plan by setting its name, price, validity period, and sales availability. Be sure to enable subscription access for each performance. They will be linked automatically based on the date and selected venue.') }}
      </p>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="create-plan-form"
        :class="{ 'create-plan-form--with-capacity': capacityIsGeneralAdmission }"
        @submit.prevent="saveSubscriptionPlan"
      >
        <div class="create-plan-details">
          <v-text-field
            v-model="subscriptionPlan.name"
            :label="adminText('organizationAll.subscriptions_plan_name_2b1074d6', 'Subscriptions plan name*')"
            required
            :rules="[v => !!v || adminText('organizationAll.name_is_required_222c72b1', 'Name is required')]"
          />

          <v-textarea v-model="subscriptionPlan.description" :label="adminText('organizationAll.description_55f8ebc8', 'Description')" rows="4" />
          <v-select
            v-model="selectedVenue"
            :items="venues"
            :label="adminText('organizationAll.venue_0b3d02df', 'Venue*')"
            item-text="displayName"
            item-value="id"
            required
            :rules="[v => !!v || adminText('organizationAll.venue_is_required_2be23251', 'Venue is required')]"
          />

          <!-- Plan cover image moved up under title/date -->
          <div class="mt-4">
            <div class="subtitle-1 mb-2">{{ adminText('organizationAll.plan_cover_image_65791027', 'Plan cover image') }}</div>
            <cover-field
              v-model="planCoverUrl"
              preserve-crop
              :upload-options="{ path: 'theater-subscription-covers', serverProcess: true }"
            />
          </div>

          <v-text-field
            v-model="subscriptionPlan.price"
            :label="adminText('organizationAll.price_eur_444903ee', 'Price (EUR)*')"
            type="number"
            required
            :rules="[
              v => (v != null && v != '' && !isNaN(v)) || adminText('organizationAll.price_must_be_a_number_e1ea37ee', 'Price must be a number'),
              v => !!v || adminText('organizationAll.price_is_required_2fa9daf0', 'Price is required'),
              v => v > 0 || adminText('organizationAll.price_must_be_greater_than_0_f0f9a3b5', 'Price must be greater than 0'),
            ]"
          />

          <div class="mt-4">
            <v-switch v-model="subscriptionPlan.active" :label="adminText('organizationAll.active_a733b809', 'Active')" @change="capacityActiveTouched = true" />

            <div class="input-container">
              <date-field v-model="subscriptionPlan.valid_from" :label="adminText('organizationAll.start_date_2fde03a5', 'Start date*')" />
              <date-field v-model="subscriptionPlan.valid_to" :label="adminText('organizationAll.end_date_dc7fcbdc', 'End date*')" />
            </div>
          </div>

          <!-- Schedule sales availability section -->
          <div class="d-flex justify-space-between align-center mb-3">
            <div class="subtitle-1">{{ adminText('organizationAll.schedule_sales_availability_bef41bf9', 'Schedule sales availability') }}</div>
            <v-switch v-model="subscriptionPlan.schedule_sales_availability" />
          </div>
          <div v-if="!subscriptionPlan.schedule_sales_availability && !capacityIsGeneralAdmission" class="grey--text">
            {{ adminText('organizationAll.otherwise_the_subscription_is_available_for_s_87eb1ca6', 'Otherwise, the subscription is available for sale immediately upon creation.') }}
          </div>
          <div v-if="subscriptionPlan.schedule_sales_availability">
            <div class="d-flex flex-wrap">
              <div class="mr-2 flex-grow-1">
                <date-field
                  v-model="subscriptionPlan.available_from"
                  :label="adminText('organizationAll.from_4a2da276', 'From*')"
                  :max="subscriptionPlan.valid_to"
                  :min="new Date().toString()"
                />
              </div>
              <div class="ml-2 flex-grow-1">
                <date-field
                  v-model="subscriptionPlan.available_to"
                  :label="adminText('organizationAll.to_c2fdddd0', 'To*')"
                  :max="subscriptionPlan.valid_to"
                  :min="new Date().toISOString()"
                />
              </div>
            </div>

            <div class="d-flex align-center mt-3">
              <v-checkbox
                v-model="subscriptionPlan.enable_pre_order"
                :label="adminText('organizationAll.enable_pre_order_for_existing_subscribers_a7317cc2', 'Enable pre-order for existing subscribers')"
                class="mt-0 mr-2"
                hide-details
              />
              <v-text-field
                v-if="subscriptionPlan.enable_pre_order"
                v-model="subscriptionPlan.pre_order_days"
                type="number"
                class="ml-2 mr-2"
                style="max-width: 80px"
                hide-details
                :disabled="!subscriptionPlan.enable_pre_order"
              />
              <span v-if="subscriptionPlan.enable_pre_order">{{ adminText('organizationAll.days_in_advance_89b29b50', 'days in advance') }}</span>
            </div>
          </div>
        </div>
        <theater-subscription-capacity
          v-if="capacityIsGeneralAdmission"
          :summary="capacitySummary"
          :loading="capacityLoading"
          :error="capacityError"
          @retry="refreshCapacityPreview"
        />
        <div class="buttons-wrapper" style="margin-top: 24px">
          <main-button
            :text="adminText('organizationAll.cancel_1507c40f', 'CANCEL')"
            type="button"
            flat
            depressed
            color="#333"
            @click="cancel"
          />
          <div class="buttons">
            <main-button
              :disabled="!valid"
              :loading="formLoading"
              :text="adminText('organizationAll.create_plan_3605843e', 'CREATE PLAN')"
              type="submit"
            />
          </div>
        </div>
      </v-form>
    </div>
    <empty-subscription-warning v-model="showEmptyCapacityWarning" :active="subscriptionPlan.active" @confirm="saveSubscriptionPlan(true)" />
    <simple-snackbar v-bind.sync="snackbar" :message-prop="organizationText(snackbar &amp;&amp; (snackbar.messageProp || snackbar.message))" />
  </v-container>
</template>

<script>
import CapacityPreview from './subscription-capacity-preview'
import TheaterSubscriptionCapacity from './TheaterSubscriptionCapacity.vue'
import EmptySubscriptionWarning from './EmptySubscriptionWarning.vue'
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { createTheaterSubscription } from '@/graphql/theater-subscriptions.graphql.js'
import gql from 'graphql-tag'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import MainButton from '@/components/shared/main-button.vue'

const GET_ORGANIZATION_VENUES_FOR_SELECT = gql`
  query GetOrganizationVenuesForSelect($organizationSlug: String!) {
    getOrganizationVenues(organizationSlug: $organizationSlug) {
      organizationVenues {
        venue {
          id
          name
          seats
          predefinedSections
        }
      }
    }
  }
`

export default {
  name: 'create-theater-subscription-plan',
  components: {
    TheaterSubscriptionCapacity,
    EmptySubscriptionWarning,
    SimpleSnackbar,
    MainButton,
  },
  mixins: [OrganizationLocalization, CapacityPreview],
  props: {
    organizationSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      capacityIsCreate: true,
      loading: false,
      formLoading: false,
      valid: true,
      validFromMenu: false,
      validToMenu: false,
      availableFromMenu: true,
      availableToMenu: false,
      scheduleSales: false,
      enablePreOrder: false,
      preOrderDays: 14,
      selectedVenue: null,
      venues: [],
      planCoverUrl: null,
      subscriptionPlan: {
        name: '',
        price: null,
        valid_from: '',
        valid_to: '',
        active: true,
        event_template_id: null,
        schedule_sales_availability: false,
        available_from: '',
        available_to: '',
        enable_pre_order: false,
        pre_order_days: 14,
      },
      organizationId: null,
      snackbar: null,
    }
  },
  async created() {
    this.loading = true // Start loading
    try {
      // Fetch venues
      const result = await this.$apollo.query({
        query: GET_ORGANIZATION_VENUES_FOR_SELECT,
        variables: {
          organizationSlug: this.organizationSlug,
        },
      })

      if (
        result.data &&
        result.data.getOrganizationVenues &&
        result.data.getOrganizationVenues.organizationVenues
      ) {
        this.venues = result.data.getOrganizationVenues.organizationVenues.map(ov =>
          this.normalizeVenue(ov.venue),
        )
      } else {
        this.venues = []
        console.warn('No venues data received or unexpected structure:', result.data)
        this.snackbar = {
          open: true,
          messageProp: 'Could not load venues for the organization.',
          color: 'warning',
        }
      }
    } catch (error) {
      console.error('Error initializing subscription plan or fetching venues:', error)
      this.venues = [] // Ensure venues is an array in case of error
      this.snackbar = {
        open: true,
        messageProp: this.organizationMessage('organizationAll.error_fetching_venues_value0_1ff78c00', 'Error fetching venues: {{value0}}', { value0: error.message || this.organizationMessage('organizationAll.unknown_error_e5fd9aa2', 'Unknown error') }),
        color: 'error',
      }
    } finally {
      this.$refs.form.validate()
      this.loading = false // End loading
    }
  },
  methods: {
    venueHasSeatMap(venue) {
      return !!(venue && venue.seats && venue.seats.length && venue.predefinedSections)
    },
    formatVenueName(venue) {
      if (!venue) return ''

      return `${venue.name} - ${this.venueHasSeatMap(venue) ? this.adminText('organizationAll.numerirano_382e78f8', 'Numerirano') : this.adminText('organizationAll.nenumerirano_f62a23af', 'Nenumerirano')}`
    },
    normalizeVenue(venue) {
      return {
        ...venue,
        id: Number(venue.id),
        displayName: this.formatVenueName(venue),
      }
    },
    cancel() {
      const slug =
        this.organizationSlug || (this.$route.params && this.$route.params.organizationSlug)
      this.$router.replace({
        name: 'theater-organization-theater-subscriptions-plan',
        params: { organizationSlug: slug },
      })
    },
    async saveSubscriptionPlan(confirmedEmpty = false) {
      if (this.formLoading) return
      if (this.$refs.form.validate()) {
        this.formLoading = true
        try {
          // Ensure valid_to is set
          if (!this.subscriptionPlan.valid_to) {
            const today = new Date()
            const nextYear = new Date(today)
            nextYear.setFullYear(today.getFullYear() + 1)
            this.subscriptionPlan.valid_to = nextYear.toISOString().substr(0, 10)
          }

          if (!this.subscriptionPlan.valid_from) {
            this.subscriptionPlan.valid_from = new Date().toISOString().substr(0, 10)
          }
          await this.$nextTick()
          if (!(await this.prepareCapacitySave(confirmedEmpty))) return

          const input = {
            name: this.subscriptionPlan.name,
            price: parseFloat(this.subscriptionPlan.price || 0),
            valid_from: new Date(this.subscriptionPlan.valid_from || new Date()),
            valid_to: new Date(
              this.subscriptionPlan.valid_to ||
                (() => {
                  const date = new Date()
                  date.setFullYear(date.getFullYear() + 1)

                  return date
                })(),
            ),
            active: this.subscriptionPlan.active !== undefined ? this.subscriptionPlan.active : true,
            venueId: parseInt(this.selectedVenue),
            organizationSlug: this.organizationSlug,
            schedule_sales_availability: Boolean(this.subscriptionPlan.schedule_sales_availability),
            enable_pre_order: this.subscriptionPlan.schedule_sales_availability
              ? Boolean(this.subscriptionPlan.enable_pre_order)
              : false,
            pre_order_days:
              this.subscriptionPlan.schedule_sales_availability && this.subscriptionPlan.enable_pre_order
                ? parseInt(this.subscriptionPlan.pre_order_days)
                : null,
            description: this.subscriptionPlan.description,
            coverUrl: this.planCoverUrl || null,
          }

          if (this.subscriptionPlan.schedule_sales_availability) {
            input.available_from = new Date(this.subscriptionPlan.available_from)
            input.available_to = new Date(this.subscriptionPlan.available_to)
          }

          await this.$apollo.mutate({
            mutation: createTheaterSubscription,
            variables: {
              input,
            },
          })

          this.snackbar = {
            open: true,
            messageProp: 'Subscription plan created successfully',
            color: 'success',
          }

          // Navigate back to the subscription plans list
          this.$router.push({
            name: 'theater-organization-theater-subscriptions-plan',
            params: { organizationSlug: this.organizationSlug },
          })
        } catch (error) {
          console.error('Error creating subscription plan:', error)
          this.snackbar = {
            open: true,
            messageProp: this.organizationMessage('organizationAll.error_creating_subscription_plan_value0_adebd963', 'Error creating subscription plan: {{value0}}', { value0: error.message || this.organizationMessage('organizationAll.unknown_error_e5fd9aa2', 'Unknown error') }),
            color: 'error',
          }
        } finally {
          this.formLoading = false
        }
      }
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  row-gap: 1.5rem;
}
.subs-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: 0;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 0.5rem;
}
.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #52616b;
  max-width: 75ch;
  line-height: 1.5;
}
.input-container > * { flex: 1; min-width: 0; }
.input-container {
  display: flex;
  justify-content: space-between;
  width: 100%;
  column-gap: 1.5rem;
}
.buttons-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}
.create-plan-form { max-width: 700px; }
.create-plan-form--with-capacity { max-width: none; display: grid; grid-template-columns: minmax(0, 1fr) minmax(340px, .9fr); gap: 32px; align-items: start; }
.create-plan-details { min-width: 0; }
.create-plan-form > .subscription-capacity { padding: 20px 0 0 28px; border-left: 1px solid #e8edf0; }
.create-plan-form > .subscription-capacity >>> .capacity-summary { flex-direction: column; align-items: stretch; gap: 10px; }
.create-plan-form > .subscription-capacity >>> .capacity-value { flex: none; }
.create-plan-form > .buttons-wrapper { grid-column: 1 / -1; padding-top: 20px; border-top: 1px solid #e8edf0; }
.buttons-wrapper >>> .main-button { font-size: 14px; min-width: 112px; height: 40px; margin: 0; }
@media (max-width: 959px) {
  .create-plan-form--with-capacity { grid-template-columns: minmax(0, 1fr); gap: 24px; }
  .create-plan-form > .subscription-capacity { padding: 24px 0 0; border-left: 0; border-top: 1px solid #e8edf0; }
}
@media (max-width: 599px) {
  .input-container { flex-direction: column; gap: 8px; }
  .buttons-wrapper { justify-content: stretch; gap: 12px; }
  .buttons-wrapper > *, .buttons-wrapper .buttons { flex: 1; }
  .buttons-wrapper >>> .main-button { width: 100%; min-height: 44px; }
}
</style>
